/* eslint-env browser */
/*
 * OrderGroove Instant Upsell — "Add to Next Order"
 * Monday #12494412574 — Implement OrderGroove Instant upsell API.
 *
 * For an eligible logged-in subscriber this REPLACES the native "Try Now" CTA
 * with a single "Add to Next Order" CTA that adds the product as a NEW recurring
 * subscription whose first fulfillment attaches to the customer's next upcoming
 * order, in one call (POST /subscriptions/iu/).
 *
 * AUTH — we do NOT sign anything. OrderGroove's on-page SDK caches a valid
 * signature in the `og_auth` cookie (customerId|ts|sig). We read it and send
 * `Authorization: JSON.stringify({public_id, sig_field, ts, sig})` — exactly what
 * OG's own SDK does. No private key or HMAC lives in the theme.
 *
 * ROLLOUT (HIY-908, 2026-09) — this module ran behind the Intelligems content
 * test 0979fd29, dormant until the variant arm set data-og-iu-test="on" on
 * <html>. That test ended at 100% rollout and the flag is gone: the module is
 * now active for every eligible customer. Eligibility itself is unchanged.
 *
 * Eligibility is gated server-side (customer tag "Active Ordergroove Subscriber");
 * here we confirm an upcoming order exists (which yields the order/payment/shipping
 * ids + date), hide the native CTA, and reveal our CTA in its place.
 *
 * NO REPEAT OFFER — a customer who has EVER subscribed to this product (any
 * status, cancelled included) is not offered it again, so the first-order
 * discount cannot be re-triggered on the same product. GET /subscriptions/
 * answers that; see hasSubscribedBefore(). Fails safe: if the lookup cannot
 * answer, the module stays hidden.
 */
(function () {
  'use strict';

  // Per-store OrderGroove offer id (the subscription offer used for instant upsell).
  // Production: the OFFER id aef5bc3290424135ba7550a446e2bfdc — the offer INSIDE
  // offer profile "PDP Instant Upsell - First Order 50% off (BG, HIY-908)", not
  // the profile id (829446dd65d84a8ea0477007ff99c2c1, which the API rejects with
  // 400 "does not exist"). OG dashboard → Flex Incentives → the profile's
  // Properties dialog → "Offers → ID aef5bc32…". Do not swap it back to the profile id.
  var OFFER_ID_BY_HOSTNAME = {
    'hiyahealth.com': 'aef5bc3290424135ba7550a446e2bfdc',
    'hiya-kids.myshopify.com': 'aef5bc3290424135ba7550a446e2bfdc',
    'hiya-dev-shop.myshopify.com': '326aeb45387b4fa5850a0edac5dca6bf',
    'hiya-staging.myshopify.com': '7a6a43d33ab348998de63489a46148af',
  };

  var GA_SEND_TO = 'G-KJXY4CR3MB';

  // Source tag (Monday #13211554501). Ordergroove records a PDP "Add to Next
  // Order" subscription and a Subscription Manager portal upsell under the same
  // subscription type, so reporting cannot tell them apart. We stamp every
  // subscription this module creates — and every GA4 event it fires — with this
  // id. The Subscription Manager portal (a separate OG app repo) stamps its own
  // upsell with 'subscription_manager', so both sides are explicitly tagged
  // rather than inferred by absence. It rides the OG Create-in-Order `extra_data`
  // field as a JSON object (see createSubscription for why an object, not a
  // string), and GA4 as the `subscription_source` param.
  var SUBSCRIPTION_SOURCE = 'pdp_instant_upsell';

  // Prior-subscription lookup budget. 5s matches the og_auth poll above; the
  // page stays usable either way because the module is hidden until it answers.
  var HISTORY_TIMEOUT_MS = 5000;
  var HISTORY_MAX_PAGES = 5;

  // Diagnostics: off by default. Enable with `window.OG_IU_DEBUG = true`.
  var DEBUG = window.OG_IU_DEBUG === true;
  function log() {
    if (DEBUG && window.console && window.console.log) {
      window.console.log.apply(window.console, ['[OG IU]'].concat([].slice.call(arguments)));
    }
  }

  /* Fire a GA4 + Intelligems event. Mirrors createEvents() in gtag-events.js. */
  function fireEvent(name, detail) {
    var params = detail || {};
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: name, properties: params });
    } catch (e) {
      /* noop */
    }
    try {
      if (typeof window.gtag === 'function') {
        var payload = {};
        for (var k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k) && params[k] !== undefined) {
            payload[k] = params[k];
          }
        }
        payload.send_to = GA_SEND_TO;
        window.gtag('event', name, payload);
      }
    } catch (e2) {
      /* noop */
    }
  }

  /* ---- Auth: reuse OrderGroove's cached signature cookie ---- */

  function readOgAuthCookie() {
    var cookies = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < cookies.length; i++) {
      if (cookies[i].indexOf('og_auth=') === 0) {
        var parts = cookies[i].slice('og_auth='.length).split('|');
        if (parts.length === 3) {
          return { sig_field: parts[0], ts: parseInt(parts[1], 10), sig: parts[2] };
        }
        return null;
      }
    }
    return null;
  }

  function buildAuthorization(merchant, og) {
    return JSON.stringify({ public_id: merchant, sig_field: og.sig_field, ts: og.ts, sig: og.sig });
  }

  /* Wait for OrderGroove's cached signature cookie inside a ~5s window. Calls
     back with the og_auth parts, or null if the SDK never cached one.

     Until the 100% rollout (2026-09) this also waited on html[data-og-iu-test="on"],
     the attribute the Intelligems content test (0979fd29) set on its variant arm
     so the module stayed dormant on control. The test is over; the flag and the
     <html> MutationObserver that watched for it are gone. Eligibility is now the
     server-side "Active Ordergroove Subscriber" gate in
     snippets/og-instant-upsell.liquid plus the upcoming-order and prior-subscription
     checks below. */
  function waitForAuth(cb) {
    var tries = 0;

    function attempt() {
      var og = readOgAuthCookie();
      if (og) return cb(og);
      if (++tries > 20) return cb(null); // ~5s
      window.setTimeout(attempt, 250);
    }

    attempt();
  }

  /* ---- Helpers ---- */

  function resolveOffer(el) {
    var byHost = OFFER_ID_BY_HOSTNAME[window.location.hostname];
    if (byHost) return byHost;
    return OFFER_ID_BY_HOSTNAME[el.dataset.ogShopDomain] || null;
  }

  function pickId(value) {
    if (!value) return null;
    if (typeof value === 'object') return value.public_id || value.id || null;
    return value;
  }

  /* OG order dates look like "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS"; parse as
     local to avoid a TZ off-by-one. */
  function parseOgDate(value) {
    if (!value) return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    var d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  function formatDate(date) {
    if (!date) return '';
    try {
      var locale = document.documentElement.lang || 'en-US';
      return date.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  /* From /orders?status=1 pick the soonest pending order and its ids/date. */
  function pickUpcomingOrder(data) {
    var list = data && data.results ? data.results : data;
    if (!Array.isArray(list) || list.length === 0) return null;

    var best = null;
    var bestTime = Infinity;
    for (var i = 0; i < list.length; i++) {
      var o = list[i];
      var d = parseOgDate(o.place || o.order_date || o.ship_date || o.start_date || o.date);
      var t = d ? d.getTime() : Infinity;
      if (t < bestTime) {
        bestTime = t;
        best = o;
      }
    }
    var chosen = best || list[0];
    var dateVal = chosen.place || chosen.order_date || chosen.ship_date || chosen.start_date || chosen.date;
    return {
      raw: chosen,
      public_id: pickId(chosen.public_id || chosen.id),
      payment: pickId(chosen.payment),
      shipping_address: pickId(chosen.shipping_address),
      date: parseOgDate(dateVal),
    };
  }

  function baseParams(d) {
    return {
      product_sku: d.ogProductSku || '',
      product_id: d.ogProductId || '',
      product_name: d.ogProductTitle || '',
      product_category: d.ogProductCategory || '',
      product_price: d.ogProductPrice ? parseFloat(d.ogProductPrice) : undefined,
      market: d.ogMarket || '',
      customer_type: 'Subscriber',
      subscription_source: SUBSCRIPTION_SOURCE,
    };
  }

  /* ---- Silent-exit diagnostics (HIY-918) ----

     Every path that leaves this module hidden used to end in a console log, so
     "not eligible by design" (already subscribed, no upcoming order) was
     indistinguishable in the data from "eligible but lost" (og_auth never
     arrived, OrderGroove rejected or dropped the call). One event with a
     `reason` splits them.

     ONE terminal outcome per PDP load, keyed on the rendered path exactly like
     `__ogSubscriberPdpView` in snippets/og-subscriber-pdp-view.liquid: a barba
     container swap back onto the same PDP mounts a fresh element, so the
     per-element `data-og-init` latch does not cover it. Revealing marks the
     path too, so no hidden event can ever follow an `instant_upsell_view`.

     Before the 100% rollout these fired on the test's variant arm only, because
     control never got past the data-og-iu-test flag. They now fire for all
     eligible traffic, so volume is not comparable across the cutover. */
  function settleOnce() {
    var path = window.location.pathname;
    if (window.__ogInstantUpsellSettled === path) return false;
    window.__ogInstantUpsellSettled = path;
    return true;
  }

  function fireHidden(d, reason, errorCode) {
    if (!settleOnce()) return;
    var params = baseParams(d);
    params.reason = reason;
    if (errorCode !== undefined && errorCode !== null) params.error_code = errorCode;
    fireEvent('instant_upsell_hidden', params);
  }

  function sessionId() {
    return 'iu-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* Read the live selection from the enclosing product form. The buy-box is a
     `variant-selector` Stimulus form whose hidden select[name="id"]
     (variant-id-targets.liquid) is kept in sync by the controller. */
  function liveSelection(el) {
    var d = el.dataset;
    var form = el.closest('form');
    var variantId = d.ogProduct;
    var variantTitle = d.ogVariantTitle || '';
    var quantity = parseInt(d.ogQuantity, 10) || 1;

    if (form) {
      var idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) {
        variantId = idInput.value;
        if (idInput.tagName === 'SELECT' && idInput.selectedIndex > -1) {
          var t = idInput.options[idInput.selectedIndex].textContent.trim();
          if (t && t !== 'Default Title') variantTitle = t;
        }
      }
      var qtyInput = form.querySelector('[name="quantity"]');
      if (qtyInput && qtyInput.value) {
        var q = parseInt(qtyInput.value, 10);
        if (q > 0) quantity = q;
      }
    }
    return { variantId: variantId, variantTitle: variantTitle, quantity: quantity };
  }

  /* Fill a copy template into `node`. Slots named in `bold` are emitted as
     <strong> — Abby's Figma sets the product name in bold inside an otherwise
     regular line — without ever handing markup to innerHTML. */
  function renderTemplate(node, tpl, values, bold) {
    node.textContent = '';
    var parts = String(tpl || '').split(/(%[A-Z]+%)/);
    for (var i = 0; i < parts.length; i++) {
      var slot = /^%([A-Z]+)%$/.exec(parts[i]);
      var text = slot ? String(values[slot[1]] == null ? '' : values[slot[1]]) : parts[i];
      if (!text) continue;
      if (slot && bold.indexOf(slot[1]) > -1) {
        var strong = document.createElement('strong');
        strong.textContent = text;
        node.appendChild(strong);
      } else {
        node.appendChild(document.createTextNode(text));
      }
    }
  }

  /* Subscription cadence from the customer's selected OG selling plan (the
     checked radio when the frequency selector is enabled, else the hidden
     default input). Plan names read "Delivered every month" / "Delivered
     every 2 months". Falls back to the server-computed default. */
  function liveFrequency(el) {
    var every = parseInt(el.dataset.ogEvery, 10) || 1;
    var form = el.closest('form');
    if (form) {
      var plan =
        form.querySelector('input[name="selling_plan"]:checked') ||
        form.querySelector('input[name="selling_plan"][type="hidden"]');
      var name = plan && (plan.dataset.planName || plan.dataset.name);
      if (name) {
        var m = /(\d+)\s*month/i.exec(name);
        every = m ? parseInt(m[1], 10) : 1;
      }
    }
    return every;
  }

  function reveal(el) {
    el.classList.remove('tw-hidden');
    el.hidden = false;
  }

  /* Hide the native "Try Now" add-to-cart button so only one CTA is present.
     The variant-selector controller re-renders that button on bundle/variant
     changes, so re-assert display:none !important via a MutationObserver. */
  function keepNativeCtaHidden(el) {
    var form = el.closest('form');
    if (!form) return;
    var btn = form.querySelector('[data-add-to-cart-button]');
    if (!btn) return;
    var hide = function () {
      if (btn.style.display !== 'none') btn.style.setProperty('display', 'none', 'important');
    };
    hide();
    try {
      new MutationObserver(hide).observe(btn, { attributes: true, attributeFilter: ['style', 'class'] });
    } catch (e) {
      /* older browsers: best-effort one-time hide */
    }
  }

  /* PDPs also render a scroll-activated sticky buy bar (product-sticky-cta
     section) with its own add-to-cart form. Hide the whole sticky section for
     eligible subscribers so "Add to Next Order" is the page's only primary CTA
     (REQ-F-004). Keyed off the sticky form id, not the section handle. */
  function keepStickyCtaHidden() {
    var forms = document.querySelectorAll('form[id^="sticky-product-form-"]');
    for (var i = 0; i < forms.length; i++) {
      var section = forms[i].closest('.shopify-section') || forms[i];
      section.style.setProperty('display', 'none', 'important');
    }
  }

  /* All products to subscribe: the main variant, plus the "Better Together"
     bundle product variant when a bundle card is selected (its variant id lives
     on the hidden [name="bundle-variant"] checkbox, checked when active). */
  function collectProducts(el) {
    var main = liveSelection(el);
    var list = [{ variantId: main.variantId, quantity: main.quantity }];
    var form = el.closest('form');
    if (form) {
      var bundle = form.querySelector('[name="bundle-variant"]');
      if (bundle && bundle.checked && bundle.value) {
        list.push({ variantId: bundle.value, quantity: main.quantity });
      }
    }
    return list;
  }

  /* POST one subscription to /subscriptions/iu/ for a given product. */
  function createSubscription(el, api, authorization, d, offer, order, p) {
    var body = {
      merchant: d.ogMerchant,
      customer: d.ogCustomer,
      session_id: sessionId(),
      offer: offer,
      product: p.variantId,
      order: order.public_id,
      payment: order.payment,
      shipping_address: order.shipping_address,
      quantity: p.quantity,
      every: liveFrequency(el),
      every_period: parseInt(d.ogEveryPeriod, 10),
      // Source tag for OG reporting (Monday #13211554501). Sent as an OBJECT, not
      // a JSON string: OG merges extra_data into a dict server-side (it adds
      // shopify_contract_id by spreading the value), so a JSON string gets spread
      // character-by-character and the reporting export stores it as
      // {"0":"{","1":"\"","2":"s",...}. An object yields the intended
      // {"source":"pdp_instant_upsell","shopify_contract_id":"..."}. (The OG REST
      // reference labels extra_data "string"; the live reporting pipeline
      // contradicts that, so we follow the observed behaviour.)
      extra_data: { source: SUBSCRIPTION_SOURCE },
    };
    log('create subscription request', { url: api + '/subscriptions/iu/', body: body });
    return fetch(api + '/subscriptions/iu/', {
      method: 'POST',
      headers: { Authorization: authorization, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.text().then(function (text) {
        var json = null;
        try {
          json = text ? JSON.parse(text) : null;
        } catch (e) {
          /* non-JSON body */
        }
        return { ok: !!(res.ok && json && json.public_id), status: res.status, text: text, json: json };
      });
    });
  }

  /* Cadence label for the live selling-plan selection ("monthly" /
     "every 2 months"). */
  function frequencyLabel(el) {
    var n = liveFrequency(el);
    if (n === 1) return el.dataset.ogFreqOne || '';
    return (el.dataset.ogFreqMany || '').replace('%COUNT%', n);
  }

  /* Render the offer copy. The intro's date comes from the fetched upcoming
     order; the cadence benefit follows the *selected* selling plan, so changing
     frequency re-renders it — HIY-908 render-report anomaly A, where that line
     was baked server-side from data-og-every and never updated while the POST
     body already carried the new `every`. */
  function renderOffer(el, dateStr) {
    var d = el.dataset;
    var intro = el.querySelector('[data-og-intro]');
    if (intro) {
      renderTemplate(intro, d.ogIntroTpl, { TITLE: d.ogProductTitle || '', DATE: dateStr || '' }, ['TITLE']);
    }
    var ships = el.querySelector('[data-og-benefit-ships]');
    if (ships && d.ogShipsTpl) {
      ships.textContent = d.ogShipsTpl.replace('%FREQUENCY%', frequencyLabel(el));
    }
  }

  /* Keep the offer copy in sync with the product form selection. */
  function watchSelection(el, dateStr) {
    var form = el.closest('form');
    if (!form) return;
    var handler = function () { renderOffer(el, dateStr); };
    form.addEventListener('change', handler);
    form.addEventListener('input', handler);
    // The +/- "Number of Kids" steppers mutate the input's .value via inline
    // onclick and fire NO input/change event, so re-render on the next tick
    // after any click inside the form.
    form.addEventListener('click', function () { window.setTimeout(handler, 0); });
  }

  /* ---- Guard: never re-offer a product the customer has already subscribed to ----

     Hannah (Hiya, 2026-08-26): "No repeat discount for returning subscribers —
     the rail hides any product a customer's ever subscribed to (including
     cancelled), so they can't re-trigger the discount on the same product via
     Instant Upsell."

     `GET /subscriptions/` is allowed under the Storefront API scope, so the same
     og_auth signature works and the customer is implied by it (the `customer`
     query param is API-user scope only). The `live` filter is deliberately NOT
     sent: it selects active (True) or inactive (False), and omitting it returns
     both, which is the only way cancelled records come back. */

  /* Variant ids that count as "this product": every variant of it, plus the
     refill variant each one swaps to after the first order. Rendered by the
     snippet from custom.swap_variant_id — a cancelled subscriber's record most
     likely carries the refill id, not the one they originally bought. */
  function productVariantIds(el) {
    var ids = {};
    var parts = (el.dataset.ogProductVariants || '').split(',');
    for (var i = 0; i < parts.length; i++) {
      var id = parts[i].trim();
      if (id) ids[id] = true;
    }
    return ids;
  }

  function isKnownId(value, ids) {
    var id = pickId(value);
    return !!id && ids[String(id).trim()] === true;
  }

  function matchesProduct(sub, ids) {
    if (!sub) return false;
    if (isKnownId(sub.product, ids)) return true;
    // Bundle subscriptions carry their variants in `components`, not in the
    // top-level `product` (docs: "New Bundle components"), and this module can
    // itself create one via the "Better Together" card — so a bundle the
    // customer already subscribed to counts too.
    if (Array.isArray(sub.components)) {
      for (var i = 0; i < sub.components.length; i++) {
        if (isKnownId(sub.components[i] && sub.components[i].product, ids)) return true;
      }
    }
    return false;
  }

  /* Resolves `{ subscribed, reason }`. `subscribed` is true if the customer has
     EVER subscribed to this product, and ALSO true whenever we cannot tell
     (error, timeout, unparseable body, missing id set) — the module must not
     re-offer a discount on a maybe. `reason` says WHICH of those it was, so the
     caller can report a real "already subscribed" separately from a lookup we
     lost (HIY-918); the fail-closed behaviour itself is unchanged. */
  function hasSubscribedBefore(el, api, authorization) {
    if (!el.dataset.ogProductVariants) {
      log('guard: no data-og-product-variants rendered; treating as subscribed');
      return Promise.resolve({ subscribed: true, reason: 'no_variants_rendered' });
    }
    var ids = productVariantIds(el);

    function readPage(url, page) {
      return fetch(url, { method: 'GET', headers: { Authorization: authorization } })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          // Documented envelope: { count, next, previous, results }, with
          // `next` an absolute URL (.../subscriptions/?page=N).
          var data = JSON.parse(text);
          var list = data && data.results ? data.results : data;
          if (!Array.isArray(list)) throw new Error('no subscription list in response');
          for (var i = 0; i < list.length; i++) {
            if (matchesProduct(list[i], ids)) {
              log('guard: prior subscription found', {
                subscription: pickId(list[i].public_id || list[i].id),
                product: pickId(list[i].product),
                live: list[i].live,
                cancelled: list[i].cancelled,
              });
              return true;
            }
          }
          if (!data || !data.next) return false;
          // Paged and still no match: keep reading, but never unboundedly. An
          // unread page could hold the match, so hitting the cap fails safe.
          if (page + 1 >= HISTORY_MAX_PAGES) throw new Error('more than ' + HISTORY_MAX_PAGES + ' pages');
          return readPage(data.next, page + 1);
        });
    }

    var lookup = readPage(api + '/subscriptions/', 0)
      .then(function (found) {
        return { subscribed: found, reason: found ? 'already_subscribed' : null };
      })
      .catch(function (err) {
        log('guard: subscription lookup failed; treating as subscribed', err && err.message);
        return { subscribed: true, reason: 'subscription_lookup_failed' };
      });

    // A lookup that never answers is the same loss as one that errored.
    var timeout = new Promise(function (resolve) {
      window.setTimeout(function () {
        resolve({ subscribed: true, reason: 'subscription_lookup_failed' });
      }, HISTORY_TIMEOUT_MS);
    });

    return Promise.race([lookup, timeout]);
  }

  /* ---- Flow ---- */

  function init(el) {
    if (el.dataset.ogInit === '1') return;
    el.dataset.ogInit = '1';

    var d = el.dataset;
    var api = d.ogApi || 'https://restapi.ordergroove.com';
    var offer = resolveOffer(el);
    if (!offer || !d.ogMerchant) return; // not configured for this store

    waitForAuth(function (og) {
      if (!og) {
        log('og_auth cookie not found; cannot authenticate (OG SDK may not have initialized)');
        fireHidden(d, 'og_auth_timeout');
        return;
      }
      var authorization = buildAuthorization(d.ogMerchant, og);
      log('init', { api: api, offer: offer, merchant: d.ogMerchant, sig_field: og.sig_field, ts: og.ts });
      loadUpcomingOrder(el, api, authorization, offer);
    });
  }

  function loadUpcomingOrder(el, api, authorization, offer) {
    var d = el.dataset;
    var cta = el.querySelector('[data-og-cta]');

    fetch(api + '/orders?status=1', { method: 'GET', headers: { Authorization: authorization } })
      .then(function (res) {
        return res.text().then(function (text) {
          return { ok: res.ok, status: res.status, text: text };
        });
      })
      .then(function (r) {
        if (!r.ok) {
          log('orders REJECTED', r.status, '->', r.text);
          fireHidden(d, 'orders_rejected', r.status);
          return;
        }
        var order = pickUpcomingOrder(JSON.parse(r.text));
        if (!order || !order.public_id) {
          // No upcoming order -> keep native CTA, stay hidden.
          fireHidden(d, 'no_upcoming_order');
          return;
        }

        return hasSubscribedBefore(el, api, authorization).then(function (guard) {
          if (guard.subscribed) {
            // Already subscribed to this product (or we could not tell). Leave
            // the native "Try Now" CTA alone and fire no VIEW — that event is the
            // impression denominator, so it must count real offers only. Which of
            // the three guard outcomes it was goes out as a hidden reason instead.
            log('guard: not offering this product again');
            fireHidden(d, guard.reason);
            return;
          }

          var dateStr = formatDate(order.date);
          keepNativeCtaHidden(el);
          keepStickyCtaHidden();
          reveal(el);
          renderOffer(el, dateStr);
          watchSelection(el, dateStr);
          settleOnce(); // this PDP resolved to a real offer; no hidden event may follow
          fireEvent('instant_upsell_view', baseParams(d));

          if (cta) {
            cta.addEventListener('click', function () {
              submit(el, api, authorization, offer, order, dateStr);
            });
          }
        });
      })
      .catch(function (err) {
        log('orders fetch failed (network/CORS/parse)', err && err.message);
        fireHidden(d, 'orders_fetch_failed');
      });
  }

  function submit(el, api, authorization, offer, order, dateStr) {
    var d = el.dataset;
    var cta = el.querySelector('[data-og-cta]');
    var errEl = el.querySelector('[data-og-error-msg]');

    if (cta.dataset.busy === '1') return;
    cta.dataset.busy = '1';
    cta.disabled = true;
    var originalLabel = cta.textContent;
    if (d.ogAddingLabel) cta.textContent = d.ogAddingLabel;
    if (errEl) errEl.hidden = true;

    var products = collectProducts(el);

    fireEvent(
      'instant_upsell_cta_click',
      Object.assign({}, baseParams(d), { pdp_url: window.location.pathname, product_count: products.length })
    );

    Promise.all(
      products.map(function (p) {
        return createSubscription(el, api, authorization, d, offer, order, p);
      })
    )
      .then(function (results) {
        var allOk = results.length > 0 && results.every(function (r) { return r && r.ok; });
        if (!allOk) {
          var failed = null;
          for (var i = 0; i < results.length; i++) {
            if (!results[i] || !results[i].ok) { failed = results[i] || {}; break; }
          }
          log('create REJECTED', failed && failed.status, '->', failed && failed.text);
          throw { type: 'api_error', code: (failed && failed.status) || '', detail: failed && failed.text };
        }
        showConfirmation(el, dateStr, products.length);
        fireEvent(
          'instant_upsell_success',
          Object.assign({}, baseParams(d), {
            subscription_id: results[0].json.public_id,
            upcoming_order_id: order.public_id,
            product_count: products.length,
          })
        );
      })
      .catch(function (err) {
        var type = (err && err.type) || 'network_error';
        var code = (err && err.code) || '';
        fireEvent(
          'instant_upsell_error',
          Object.assign({}, baseParams(d), { error_type: type, error_code: code })
        );
        if (errEl) {
          errEl.textContent = d.ogError || 'Something went wrong. Please try again.';
          errEl.hidden = false;
        }
        cta.disabled = false;
        cta.dataset.busy = '0';
        cta.textContent = originalLabel;
      });
  }

  /* The kid-count lead of a Hiya variant title: everything up to the first
     " - " or "|", trimmed. "2 Kids - $14/bottle (Reg: $28)" -> "2 Kids".
     Empty / "Default Title" yields "" so the caller falls back to the count. */
  function variantPortion(title) {
    var t = String(title || '').trim();
    if (!t || t === 'Default Title') return '';
    return t.split(/ - |\|/)[0].trim();
  }

  /* Swap the offer (CTA + benefits) for the success confirmation. */
  function showConfirmation(el, dateStr, count) {
    var d = el.dataset;
    var confirmText = el.querySelector('[data-og-confirmation-text]');
    if (confirmText) {
      var multi = count > 1;
      // "%ITEM%" is the bold lead of the sentence. For a single product, lead
      // with the kid-count from the live variant title ("(2 Kids) Kids Daily
      // Multivitamin") when we have one — the count is always 1 here and reads
      // wrong when 2 kids were selected (HIY-908 Hannah 2026-08-28). Bundle
      // path keeps "(2) items".
      var portion = multi ? '' : variantPortion(liveSelection(el).variantTitle);
      var item;
      if (portion && d.ogConfirmItemVariantTpl) {
        item = String(d.ogConfirmItemVariantTpl)
          .replace('%VARIANT%', portion)
          .replace('%TITLE%', d.ogProductTitle || '');
      } else {
        item = String((multi ? d.ogConfirmItemsTpl : d.ogConfirmItemTpl) || '')
          .replace('%COUNT%', count)
          .replace('%TITLE%', d.ogProductTitle || '');
      }
      var tpl = multi && d.ogConfirmMultiTpl ? d.ogConfirmMultiTpl : d.ogConfirmTpl;
      renderTemplate(confirmText, tpl, { ITEM: item, DATE: dateStr || '' }, ['ITEM']);
    }
    var cta = el.querySelector('[data-og-cta]');
    // .og-iu__cta is display:flex; the card keeps its border and reflows around
    // the confirmation. !important guards against any theme rule winning here.
    if (cta) cta.style.setProperty('display', 'none', 'important');
    var offerEl = el.querySelector('[data-og-offer]');
    if (offerEl) {
      offerEl.hidden = true;
      offerEl.classList.add('tw-hidden');
    }
    var confirmEl = el.querySelector('[data-og-confirmation]');
    if (confirmEl) {
      confirmEl.hidden = false;
      confirmEl.classList.remove('tw-hidden');
    }
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-og-instant-upsell]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init after barba SPA navigations (theme uses barba).
  window.addEventListener('barba:afterEnter', initAll);
})();
